// frontend/src/components/InlineFieldWithInfo.tsx
import React from "react";
import { InfoTooltip, type InfoTooltipProps } from "./InfoTooltip";

export type InlineFieldWithInfoProps = {
  children: React.ReactNode;
  help: React.ReactNode;
  /** 'aside' docks the icon outside the field (default). 'overlay' places it inside the field's right edge. */
  mode?: "overlay" | "aside";
  /** Tooltip placement relative to the icon. */
  placement?: "right" | "left" | "top" | "bottom";
  /** Optional overrides for the tooltip trigger/panel (rarely needed). */
  infoProps?: Partial<InfoTooltipProps>;
};

const DEFAULTS = {
  mode: "aside" as const,
  placement: "right" as const,
  infoProps: {
    size: 22,                     // compact icon
    panelMaxWidth: "xl" as const, // wide help panel (~640px)
    panelTextClassName: "text-xs" // compact text
  } satisfies Partial<InfoTooltipProps>
};

export function InlineFieldWithInfo({
  children,
  help,
  mode,
  placement,
  infoProps,
}: InlineFieldWithInfoProps) {
  const effMode = mode ?? DEFAULTS.mode;
  const effPlacement = placement ?? DEFAULTS.placement;
  const effInfoProps: Partial<InfoTooltipProps> = { ...DEFAULTS.infoProps, ...(infoProps ?? {}) };

  if (effMode === "aside") {
    return (
      <div className="relative">
        {children}
        <div className="absolute left-full top-1/2 ml-2 -translate-y-1/2">
          <InfoTooltip content={help} placement={effPlacement} {...effInfoProps} />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {children}
      <div className="pointer-events-auto absolute right-2 top-1/2 -translate-y-1/2">
        <InfoTooltip content={help} placement={effPlacement} {...effInfoProps} />
      </div>
    </div>
  );
}